import {Button} from '@dropbox/dig-components/buttons';
import {UIIcon} from '@dropbox/dig-icons';
import {AddLine} from '@dropbox/dig-icons/assets';
import {useQueryClient} from '@tanstack/react-query';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {OptionalTooltip} from 'components/DSYS/OptionalTooltip';
import {isClosedStatus} from 'components/DSYS/StatusButtonIcon';
import {ROUTE_PATHS} from 'constant';
import {t} from 'i18next';
import {useCreateSwitchboard, useWorkstream} from 'views/workstreams/hooks';

import {workstreamDataKeys} from './hooks';

export const invalidateWorkstreamQueries = async (
  queryClient: ReturnType<typeof useQueryClient>
) => {
  await queryClient.invalidateQueries({
    queryKey: [...workstreamDataKeys.all, 'workstreams'],
    exact: false,
  });
};

export const openPlanningDocInNewTab = (editorDocId: string) => {
  window.open(
    ROUTE_PATHS.PLANNING_DOC.replace(':id', editorDocId),
    '_blank',
    'noopener,noreferrer'
  );
};

export const logSwitchboardResourceAction = ({
  source,
  type,
  workstreamId,
}: {
  source: string;
  type: 'switchboard_open' | 'switchboard_create';
  workstreamId: number;
}) => {
  analyticsLogger().logEvent('WORKSTREAM_RESOURCE_CLICKED', {
    source,
    type,
    workstream_id: workstreamId,
    v2: true,
  });
};

export const CreateSwitchboardZeroStateTrigger = ({
  workstreamId,
  analyticsSource,
}: {
  workstreamId: number;
  analyticsSource: string;
}) => {
  const queryClient = useQueryClient();
  const {mutateAsync: createSwitchboard, isPending} = useCreateSwitchboard(workstreamId);
  const {workstream} = useWorkstream(workstreamId);

  const handleCreateSwitchboard = async () => {
    logSwitchboardResourceAction({
      source: analyticsSource,
      type: 'switchboard_create',
      workstreamId,
    });
    const response = await createSwitchboard();
    await invalidateWorkstreamQueries(queryClient);
    if (response?.id) {
      openPlanningDocInNewTab(response.id);
    }
  };

  const latestStatus = workstream?.updates?.[0]?.status;
  const isClosedWorkstream = latestStatus ? isClosedStatus(latestStatus) : false;

  return (
    <OptionalTooltip enabled={isClosedWorkstream} title={t('workstream_is_closed_tooltip')}>
      <Button
        variant="outline"
        size="small"
        disabled={isPending || isClosedWorkstream}
        onClick={handleCreateSwitchboard}
        withIconStart={<UIIcon src={AddLine} />}
      >
        {t('create_switchboard')}
      </Button>
    </OptionalTooltip>
  );
};
